import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, Loader2, Globe } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHONES = [
  "+225 05 05 35 27 27",
  "+225 07 79 14 85 08",
  "+225 07 58 35 91 32",
];

const MAP_SRC =
  "https://www.google.com/maps?q=Treichville+Avenue+13+Rue+40+Abidjan&output=embed";

export default function Contact() {
  const [form, setForm] = useState({ nom: "", telephone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.telephone || !form.email || !form.message) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé avec succès. Nous vous recontactons rapidement.");
      setForm({ nom: "", telephone: "", email: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Erreur lors de l'envoi. Réessayez.";
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left - Info */}
          <div className="lg:col-span-5">
            <div className="kms-overline mb-4 flex items-center gap-4">
              <span className="font-heading font-black text-sm text-[#D90429]">08</span>
              <span className="h-px w-12 bg-[#D90429]" />
              <span>Contact</span>
            </div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#0A1128] leading-[1.05]">
              Parlons de votre <span className="text-[#D90429]">projet</span>.
            </h2>
            <p className="mt-6 text-[#6B7280] text-base sm:text-lg">
              Une question ? Un besoin d'avitaillement ou de logistique ? Notre
              équipe est à votre disposition.
            </p>

            <div className="mt-12 space-y-8">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Phone size={18} className="text-[#D90429]" />
                  <div className="kms-overline" style={{ color: "#0A1128" }}>Téléphone</div>
                </div>
                <div className="space-y-1">
                  {PHONES.map((p) => (
                    <a
                      key={p}
                      href={`tel:${p.replace(/\s/g, "")}`}
                      className="block font-heading font-bold text-lg text-[#0A1128] hover:text-[#D90429] transition-colors"
                    >
                      {p}
                    </a>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Mail size={18} className="text-[#D90429]" />
                  <div className="kms-overline" style={{ color: "#0A1128" }}>E-mail</div>
                </div>
                <a
                  href="mailto:supportkms@kmsserviceplus.com"
                  className="font-heading font-bold text-lg text-[#0A1128] hover:text-[#D90429] transition-colors break-all"
                >
                  supportkms@kmsserviceplus.com
                </a>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Globe size={18} className="text-[#D90429]" />
                  <div className="kms-overline" style={{ color: "#0A1128" }}>Site web</div>
                </div>
                <a
                  href="https://www.kmsserviceplus.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-heading font-bold text-lg text-[#0A1128] hover:text-[#D90429] transition-colors"
                >
                  www.kmsserviceplus.com
                </a>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <MapPin size={18} className="text-[#D90429]" />
                  <div className="kms-overline" style={{ color: "#0A1128" }}>Adresse</div>
                </div>
                <p className="font-heading font-bold text-lg text-[#0A1128] leading-tight">
                  Abidjan – Treichville<br />Avenue 13 Rue 40
                </p>
                <p className="mt-1 text-sm text-[#6B7280]">05 BP 1972 Abidjan 05</p>
              </div>
            </div>

            <div className="mt-12 border border-gray-200 overflow-hidden">
              <iframe
                title="KMS Services Plus - Localisation"
                src={MAP_SRC}
                className="w-full h-72 grayscale hover:grayscale-0 transition-all duration-500"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right - Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#F9FAFB] border border-gray-200 text-[#0A1128] p-8 sm:p-12 shadow-xl"
            >
              <h3 className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-[#0A1128]">
                Envoyez-nous un message
              </h3>
              <p className="mt-2 text-[#6B7280] text-sm">
                Nous répondons sous 24h ouvrées.
              </p>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                <div>
                  <label className="kms-label">Nom *</label>
                  <input
                    type="text"
                    name="nom"
                    value={form.nom}
                    onChange={onChange}
                    data-testid="contact-input-nom"
                    className="kms-input"
                    placeholder="Votre nom"
                    required
                  />
                </div>
                <div>
                  <label className="kms-label">Téléphone *</label>
                  <input
                    type="tel"
                    name="telephone"
                    value={form.telephone}
                    onChange={onChange}
                    data-testid="contact-input-telephone"
                    className="kms-input"
                    placeholder="+225 ..."
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="kms-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="contact-input-email"
                    className="kms-input"
                    placeholder="vous@exemple.com"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="kms-label">Message *</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-input-message"
                    rows="5"
                    className="kms-input resize-none"
                    placeholder="Décrivez votre besoin..."
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="mt-10 inline-flex items-center gap-2 bg-[#0A1128] text-white px-8 py-4 font-bold tracking-wide hover:bg-[#D90429] transition-all duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {loading ? "Envoi en cours..." : "Envoyer une demande"}
              </button>

              <p className="mt-6 text-xs text-[#9CA3AF]">
                En soumettant ce formulaire, vous acceptez d'être recontacté
                par KMS Services Plus.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
